import React from 'react';
import { useTranslation } from 'localization';
import { ApproachOrbitConfig } from 'types/approach';
import { ScheduleDefinition, KeyDateDefinition } from 'types/orbit';
import './OrbitSummaryMolecule.scss';

export interface OrbitSummaryMoleculeProps {
  orbit: ApproachOrbitConfig;
  className?: string;
}

const SCHEDULE_TYPE_I18N_MAP: Record<string, string> = {
  rrule: 'marketplace.approachDetail.orbit.scheduleTypes.rrule',
  cron: 'marketplace.approachDetail.orbit.scheduleTypes.cron',
  reference: 'marketplace.approachDetail.orbit.scheduleTypes.reference',
};

const OrbitSummaryMolecule: React.FC<OrbitSummaryMoleculeProps> = ({
  orbit,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = ['marketplace-orbit-summary-molecule', className]
    .filter(Boolean)
    .join(' ');

  const schedules: [string, ScheduleDefinition][] = orbit.inlineDefinition?.schedules
    ? Object.entries(orbit.inlineDefinition.schedules)
    : [];

  const keyDates: KeyDateDefinition[] = orbit.inlineDefinition?.keyDates ?? [];

  return (
    <div className={classes}>
      <div className="marketplace-orbit-summary-molecule__source">
        <i className="pi pi-sync" />
        <span>
          {orbit.source === 'template'
            ? t('marketplace.approachDetail.orbit.templateSource', {
                name: orbit.templateSystemName ?? '',
              })
            : t('marketplace.approachDetail.orbit.inlineSource')}
        </span>
      </div>

      {(orbit.startDate || orbit.endDate) && (
        <div className="marketplace-orbit-summary-molecule__dates">
          {orbit.startDate && (
            <span className="marketplace-orbit-summary-molecule__date">
              <i className="pi pi-calendar" />
              {t('marketplace.approachDetail.orbit.startDate', {
                date: orbit.startDate,
              })}
            </span>
          )}
          {orbit.endDate && (
            <span className="marketplace-orbit-summary-molecule__date">
              <i className="pi pi-calendar" />
              {t('marketplace.approachDetail.orbit.endDate', {
                date: orbit.endDate,
              })}
            </span>
          )}
        </div>
      )}

      {schedules.length > 0 ? (
        <div className="marketplace-orbit-summary-molecule__schedules">
          {schedules.map(([name, schedule]) => (
            <div key={name} className="marketplace-orbit-summary-molecule__schedule">
              <div className="marketplace-orbit-summary-molecule__schedule-header">
                <i className="pi pi-clock" />
                <span>
                  {t('marketplace.approachDetail.orbit.scheduleName', { name })}
                </span>
              </div>
              <span className="marketplace-orbit-summary-molecule__schedule-type">
                {t(
                  SCHEDULE_TYPE_I18N_MAP[schedule.type] ??
                    SCHEDULE_TYPE_I18N_MAP['rrule']
                )}
              </span>
            </div>
          ))}
        </div>
      ) : (
        orbit.source === 'inline' && (
          <span className="marketplace-orbit-summary-molecule__empty">
            {t('marketplace.approachDetail.orbit.noSchedules')}
          </span>
        )
      )}

      {keyDates.length > 0 && (
        <div className="marketplace-orbit-summary-molecule__key-dates-section">
          <span className="marketplace-orbit-summary-molecule__key-dates-label">
            <i className="pi pi-flag" />
            {t('marketplace.approachDetail.orbit.keyDates')}
          </span>
          <div className="marketplace-orbit-summary-molecule__key-dates-list">
            {keyDates.map((keyDate) => (
              <div
                key={keyDate.name}
                className="marketplace-orbit-summary-molecule__key-date"
              >
                <span className="marketplace-orbit-summary-molecule__key-date-name">
                  {t('marketplace.approachDetail.orbit.keyDateName', {
                    name: keyDate.localizationKey ?? keyDate.name,
                  })}
                </span>
                <span className="marketplace-orbit-summary-molecule__key-date-source">
                  {t('marketplace.approachDetail.orbit.keyDateSource', {
                    source: keyDate.source,
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrbitSummaryMolecule;
